/**
 * HR Page
 *
 * Main HR management page with tabs for:
 * - Team members
 * - Sick leaves
 * - Vacation requests
 * - Work hours
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Badge,
  Chip,
  Card,
  CardContent,
  CardActions,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Stack,
  Skeleton,
  type SelectChangeEvent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import SickIcon from '@mui/icons-material/Sick';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BuildIcon from '@mui/icons-material/Build';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useNotify } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { useAuthContext } from '@/hooks/useAuth';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { ConfirmDialog, UserAvatar } from '@/components/common';
import { WorkHoursTab } from '@/components/hr/WorkHoursTab';
import { SkillsTab } from './skills/SkillsTab';
import { ShiftPatternsTab } from './shift-patterns/ShiftPatternsTab';
import { ShiftEndConfirmationsTab } from './shift-end-confirmations';
import { resolveHrTab, type HrTab } from './hrTabs';
import {
  TeamMembersList,
  AddTeamMemberDialog,
  EditTeamMemberDialog,
  MobileJoinQrDialog,
  type MobileJoinQrTarget,
  type TeamMember,
} from '@/components/settings';
import { api } from '@/lib/api';
import type { StaffRole } from '@/types';
import type { InvitationApiResponse } from '@/features/settings/SettingsPage';
import { formatDate } from '@/utils/locale';
import {
  useSickLeaves,
  useVacationRequests,
  useEndSickLeave,
  useCancelSickLeave,
  useApproveVacationRequest,
  useDeclineVacationRequest,
  useCancelVacationRequest,
  getStatusColor,
  type SickLeave,
  type VacationRequest,
  type SickLeaveStatus,
  type VacationRequestStatus,
  type VacationType,
} from '@/hooks/useHR';

interface TabPanelProps {
  children?: React.ReactNode;
  index: HrTab;
  value: HrTab;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

/** Map backend role values to frontend StaffRole */
function mapRole(backendRole: string): StaffRole {
  switch (backendRole) {
    case 'admin': return 'owner';
    case 'reception': return 'receptionist';
    default: return backendRole as StaffRole;
  }
}

function mapUserToStaffMember(user: UserApiResponse): TeamMember {
  return {
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    phone: user.phone ?? '',
    role: mapRole(user.role),
    roleId: user.roleId ?? null,
    roleName: user.roleName ?? null,
    isActive: user.isActive,
    isSchedulable: user.isSchedulable ?? true,
    createdAt: user.createdAt,
    lastLoginAt: user.lastLoginAt ?? undefined,
  };
}

interface UserApiResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  role: string;
  roleId?: string | null;
  roleName?: string | null;
  isActive: boolean;
  /** Counts toward workshop capacity (#3120); older API builds omit it. */
  isSchedulable?: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

interface UsersListResponse {
  users: UserApiResponse[];
  total: number;
  page: number;
  pageSize: number;
}

interface InvitationsListResponse {
  invitations: InvitationApiResponse[];
}

/**
 * `POST /users/invitations/:id/resend`.
 *
 * Both optional-looking details matter because **resend rotates the token**, so
 * this response holds the only copy of it and nothing in it may be dropped:
 * `mobileJoinUrl` is absent on a staff-api older than #1244 E1, and `emailSent`
 * is false whenever best-effort delivery failed. Either way the invitee's
 * previous link is already dead, so the dialog opens regardless and falls back
 * to `invitationUrl`.
 */
interface ResendInvitationResponse {
  email: string;
  expiresAt: string;
  /** The rotated web link — the dialog's fallback when the QR is unusable. */
  invitationUrl: string;
  mobileJoinUrl?: string;
  emailSent: boolean;
}

export default function HRPage() {
  const { t } = useTranslation('hr');
  const { t: tc } = useTranslation('common');
  const { t: tSettings } = useTranslation('settings');
  const notify = useNotify();
  const isAdmin = useAuthStore((s) => s.hasRole(['admin']));
  const { data: authContext, isError: authContextError } = useAuthContext();
  const authContextLoading = authContext === undefined && authContextError !== true;
  const canResetPassword =
    isAdmin && !authContextLoading && authContext?.oidcEnabled !== true;

  // HR consolidates team members, sick leaves, vacation requests, work
  // hours, skills, shift patterns and (since issue #532) shift-end
  // confirmation review into a single page with URL-driven tabs (?tab=…)
  // so every tab stays deep-linkable. Every tab shares the same
  // hr.view_team gate (enforced at the route), so there's no per-tab
  // permission clamping here.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveHrTab(searchParams.get('tab'));
  const handleTabChange = useCallback((_: React.SyntheticEvent, value: HrTab) => {
    const next = new URLSearchParams(searchParams);
    // 'team-members' is the default tab and carries no ?tab param so its URL stays clean.
    if (value === 'team-members') {
      next.delete('tab');
    } else {
      next.set('tab', value);
    }
    if (value !== 'skills') {
      next.delete('skillUser');
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleManageSkills = useCallback((member: TeamMember) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'skills');
    next.set('skillUser', member.id);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // --- Team members state ---
  const [staffMembers, setStaffMembers] = useState<TeamMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [membersError, setMembersError] = useState(false);
  const [invitations, setInvitations] = useState<InvitationApiResponse[]>([]);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [editMember, setEditMember] = useState<TeamMember | null>(null);
  const [qrTarget, setQrTarget] = useState<MobileJoinQrTarget | null>(null);
  /** A ref, not state: two rapid confirms would both read a stale `false`. */
  const showQrPendingRef = useRef(false);

  // Deep link /hr?member=<userId> (dashboard recent activity, #3937) opens the
  // same dialog as clicking the member's row once the list has loaded. The
  // param is consumed so closing the dialog does not reopen it.
  const memberParam = searchParams.get('member');
  useEffect(() => {
    if (!memberParam || membersLoading) return;
    const member = staffMembers.find((m) => m.id === memberParam);
    if (member) setEditMember(member);
    const next = new URLSearchParams(searchParams);
    next.delete('member');
    setSearchParams(next, { replace: true });
  }, [memberParam, membersLoading, staffMembers, searchParams, setSearchParams]);

  const loadTeamMembers = async (): Promise<boolean> => {
    try {
      const response = await api.get<UsersListResponse>('/users', {
        params: { pageSize: 100 },
      });
      setStaffMembers((response.users ?? []).map(mapUserToStaffMember));
      setMembersError(false);
      return true;
    } catch {
      setStaffMembers([]);
      setMembersError(true);
      return false;
    } finally {
      setMembersLoading(false);
    }
  };

  const loadInvitations = async () => {
    try {
      const response = await api.get<InvitationsListResponse>('/users/invitations');
      setInvitations(response.invitations.filter((inv) => inv.status === 'pending'));
    } catch {
      // Ignore errors — invitations are supplementary
    }
  };

  useEffect(() => {
    void loadTeamMembers();
    void loadInvitations();
  }, []);

  const handleResendInvitation = async (invitationId: string) => {
    try {
      await api.post(`/users/invitations/${invitationId}/resend`, {});
      notify.success(tSettings('inviteResent'));
      void loadInvitations();
    } catch {
      notify.error(tSettings('inviteResendFailed'));
    }
  };

  /**
   * Show the mobile join QR for a pending invitation (#1244 E4). The stored
   * token is a hash, so the only way to render a code is to mint a new one —
   * resend does exactly that and returns the fresh `mobileJoinUrl`. The user has
   * already confirmed the rotation in TeamMembersList.
   */
  const handleShowInviteQr = async (invitationId: string) => {
    // Authoritative dedupe. The confirmation also disables itself while this is
    // in flight, but every call rotates the token, so a second one slipping
    // through would invalidate the code the first is about to display.
    if (showQrPendingRef.current) return;
    showQrPendingRef.current = true;
    try {
      const result = await api.post<ResendInvitationResponse>(
        `/users/invitations/${invitationId}/resend`,
        {}
      );
      // Open the dialog even without a `mobileJoinUrl`: the token has already
      // rotated, so the web link in the response is the only remaining way in
      // and dropping it here would strand the invitation. The dialog says why
      // there is no QR.
      setQrTarget({
        email: result.email,
        mobileJoinUrl: result.mobileJoinUrl,
        invitationUrl: result.invitationUrl,
        expiresAt: result.expiresAt,
        emailSent: result.emailSent,
      });
      void loadInvitations();
    } catch {
      notify.error(tSettings('inviteResendFailed'));
    } finally {
      showQrPendingRef.current = false;
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await api.delete(`/users/invitations/${invitationId}`);
      notify.success(tSettings('inviteCancelled'));
      void loadInvitations();
    } catch {
      notify.error(tSettings('inviteCancelFailed'));
    }
  };

  const handleAddMember = () => {
    setAddMemberOpen(true);
  };

  const handleAddMemberSuccess = () => {
    setAddMemberOpen(false);
    void loadTeamMembers();
    void loadInvitations();
  };

  const handleEditMember = (member: TeamMember) => {
    setEditMember(member);
  };

  const handleToggleMemberActive = async (memberId: string, isActive: boolean) => {
    try {
      await api.patch(`/users/${memberId}`, { isActive });
    } catch {
      notify.error(
        isActive ? tSettings('memberActivateFailed') : tSettings('memberDeactivateFailed')
      );
      return;
    }
    if (await loadTeamMembers()) {
      notify.success(
        tSettings('memberUpdated'),
        isActive ? tSettings('memberActivated') : tSettings('memberDeactivated')
      );
    }
  };

  /** Whether the member counts toward workshop capacity (#3120). */
  const handleToggleMemberSchedulable = async (memberId: string, isSchedulable: boolean) => {
    try {
      await api.patch(`/users/${memberId}`, { isSchedulable });
    } catch {
      notify.error(tSettings('memberSchedulableFailed'));
      return;
    }
    if (await loadTeamMembers()) {
      notify.success(
        tSettings('memberUpdated'),
        isSchedulable ? tSettings('memberSchedulableOn') : tSettings('memberSchedulableOff')
      );
    }
  };

  /**
   * DELETE /users/:id is a soft-disable (`isActive: false`), not a hard erase.
   * Reload after success so the member stays on the team as inactive and can
   * be reactivated with PATCH /users/:id `{ isActive: true }`.
   */
  const handleDeleteMember = async (memberId: string) => {
    try {
      await api.delete(`/users/${memberId}`);
    } catch {
      notify.error(tSettings('memberDeleteFailed'));
      return;
    }
    if (await loadTeamMembers()) {
      notify.success(tSettings('memberDeleted'), tSettings('memberDeletedDesc'));
    }
  };

  // --- Sick leaves state ---
  const [sickLeaveSearch, setSickLeaveSearch] = useState('');
  const [sickLeaveStatusFilter, setSickLeaveStatusFilter] = useState<SickLeaveStatus | 'all'>('all');

  // Vacation requests state
  const [vacationSearch, setVacationSearch] = useState('');
  const [vacationStatusFilter, setVacationStatusFilter] = useState<VacationRequestStatus | 'all'>('pending');
  const [vacationTypeFilter, setVacationTypeFilter] = useState<VacationType | 'all'>('all');

  // Dialog state
  const [endSickLeaveDialog, setEndSickLeaveDialog] = useState<SickLeave | null>(null);
  const [declineDialog, setDeclineDialog] = useState<VacationRequest | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [cancelSickLeaveId, setCancelSickLeaveId] = useState<string | null>(null);
  const [approveVacationId, setApproveVacationId] = useState<string | null>(null);
  const [cancelVacationId, setCancelVacationId] = useState<string | null>(null);

  // Queries
  const sickLeavesQuery = useSickLeaves({
    status: sickLeaveStatusFilter === 'all' ? undefined : sickLeaveStatusFilter,
    limit: 50,
  });

  const vacationRequestsQuery = useVacationRequests({
    status: vacationStatusFilter === 'all' ? undefined : vacationStatusFilter,
    vacationType: vacationTypeFilter === 'all' ? undefined : vacationTypeFilter,
    limit: 50,
  });

  // Mutations
  const endSickLeaveMutation = useEndSickLeave();
  const cancelSickLeaveMutation = useCancelSickLeave();
  const approveVacationMutation = useApproveVacationRequest();
  const declineVacationMutation = useDeclineVacationRequest();
  const cancelVacationMutation = useCancelVacationRequest();

  // Computed data
  const sickLeaves = useMemo(() => {
    const allData = sickLeavesQuery.data?.pages.flatMap((p) => p.data) ?? [];
    if (!sickLeaveSearch) return allData;
    const query = sickLeaveSearch.toLowerCase();
    return allData.filter(
      (sl) =>
        sl.employeeName?.toLowerCase().includes(query) ||
        sl.employeeEmail?.toLowerCase().includes(query) ||
        sl.reason?.toLowerCase().includes(query)
    );
  }, [sickLeavesQuery.data, sickLeaveSearch]);

  const vacationRequests = useMemo(() => {
    const allData = vacationRequestsQuery.data?.pages.flatMap((p) => p.data) ?? [];
    if (!vacationSearch) return allData;
    const query = vacationSearch.toLowerCase();
    return allData.filter(
      (vr) =>
        vr.employeeName?.toLowerCase().includes(query) ||
        vr.employeeEmail?.toLowerCase().includes(query) ||
        vr.notes?.toLowerCase().includes(query)
    );
  }, [vacationRequestsQuery.data, vacationSearch]);

  const pendingVacationCount = useMemo(
    () => vacationRequests.filter((vr) => vr.status === 'pending').length,
    [vacationRequests]
  );

  const activeSickLeaveCount = useMemo(
    () => sickLeaves.filter((sl) => sl.status === 'active').length,
    [sickLeaves]
  );

  // Handlers
  const handleEndSickLeave = async () => {
    if (!endSickLeaveDialog) return;
    try {
      await endSickLeaveMutation.mutateAsync({
        id: endSickLeaveDialog.id,
        data: {},
      });
      notify.success(t('sickLeaveEnded'));
      setEndSickLeaveDialog(null);
    } catch {
      notify.error(t('errorEndingSickLeave'));
    }
  };

  const handleCancelSickLeave = async (id: string) => {
    try {
      await cancelSickLeaveMutation.mutateAsync(id);
      notify.success(t('sickLeaveCancelled'));
    } catch {
      notify.error(t('errorCancellingSickLeave'));
    }
  };

  const handleApproveVacation = async (id: string) => {
    try {
      await approveVacationMutation.mutateAsync({ id });
      notify.success(t('vacationApproved'));
    } catch {
      notify.error(t('errorApprovingVacation'));
    }
  };

  const handleDeclineVacation = async () => {
    if (!declineDialog) return;
    try {
      await declineVacationMutation.mutateAsync({
        id: declineDialog.id,
        data: { reason: declineReason },
      });
      notify.success(t('vacationDeclined'));
      setDeclineDialog(null);
      setDeclineReason('');
    } catch {
      notify.error(t('errorDecliningVacation'));
    }
  };

  const handleCancelVacation = async (id: string) => {
    try {
      await cancelVacationMutation.mutateAsync(id);
      notify.success(t('vacationCancelled'));
    } catch {
      notify.error(t('errorCancellingVacation'));
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        {t('title')}
      </Typography>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          value="team-members"
          icon={<GroupIcon />}
          iconPosition="start"
          label={t('teamMembers')}
        />
        <Tab
          value="sick-leaves"
          icon={<SickIcon />}
          iconPosition="start"
          label={
            <Badge badgeContent={activeSickLeaveCount} color="warning" sx={{ pr: activeSickLeaveCount > 0 ? 2 : 0 }}>
              {t('sickLeaves')}
            </Badge>
          }
        />
        <Tab
          value="vacation-requests"
          icon={<BeachAccessIcon />}
          iconPosition="start"
          label={
            <Badge badgeContent={pendingVacationCount} color="primary" sx={{ pr: pendingVacationCount > 0 ? 2 : 0 }}>
              {t('vacationRequests')}
            </Badge>
          }
        />
        <Tab
          value="work-hours"
          icon={<AccessTimeIcon />}
          iconPosition="start"
          label={t('workHours')}
        />
        <Tab
          value="skills"
          icon={<BuildIcon />}
          iconPosition="start"
          label={t('skills')}
        />
        <Tab
          value="shift-patterns"
          icon={<ScheduleIcon />}
          iconPosition="start"
          label={t('shiftPatterns')}
        />
        <Tab
          value="shift-end-confirmations"
          icon={<AssignmentLateIcon />}
          iconPosition="start"
          label={t('shiftEndConfirmations.tab')}
        />
      </Tabs>

      {/* Team Members Tab */}
      <TabPanel value={activeTab} index="team-members">
        {membersLoading ? (
          <Stack spacing={2}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" height={72} sx={{ borderRadius: 2 }} />
            ))}
          </Stack>
        ) : membersError ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setMembersLoading(true);
                  void loadTeamMembers();
                }}
              >
                {tc('errors.refresh')}
              </Button>
            }
          >
            {tSettings('membersLoadFailed')}
          </Alert>
        ) : (
          <TeamMembersList
            members={staffMembers}
            invitations={invitations}
            onAdd={handleAddMember}
            onEdit={handleEditMember}
            onToggleActive={(id, isActive) => void handleToggleMemberActive(id, isActive)}
            onToggleSchedulable={(id, isSchedulable) => void handleToggleMemberSchedulable(id, isSchedulable)}
            onDelete={(id) => void handleDeleteMember(id)}
            onResendInvitation={(id) => void handleResendInvitation(id)}
            onCancelInvitation={(id) => void handleCancelInvitation(id)}
            onShowInviteQr={handleShowInviteQr}
            onManageSkills={handleManageSkills}
            canResetPassword={canResetPassword}
          />
        )}
      </TabPanel>

      {/* Sick Leaves Tab */}
      <TabPanel value={activeTab} index="sick-leaves">
        {/* Toolbar */}
        <Box
          // Bottom-aligned: a label-less search field is 16px shorter than a
          // labelled one (the theme reserves that room above the box), so
          // centring leaves the search box floating above its neighbours (#2259).
          sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 3, flexWrap: 'wrap' }}
        >
          <TextField
            size="small"
            placeholder={t('searchEmployee')}
            value={sickLeaveSearch}
            onChange={(e) => setSickLeaveSearch(e.target.value)}
            sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: 250 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              },
            }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('status')}</InputLabel>
            <Select
              value={sickLeaveStatusFilter}
              label={t('status')}
              onChange={(e: SelectChangeEvent) =>
                setSickLeaveStatusFilter(e.target.value as SickLeaveStatus | 'all')
              }
            >
              <MenuItem value="all">{t('allStatuses')}</MenuItem>
              <MenuItem value="active">{t('active')}</MenuItem>
              <MenuItem value="ended">{t('ended')}</MenuItem>
              <MenuItem value="cancelled">{t('cancelled')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Sick Leaves List */}
        {sickLeavesQuery.isLoading ? (
          <Stack spacing={2}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            ))}
          </Stack>
        ) : sickLeavesQuery.isError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void sickLeavesQuery.refetch()}>
                {tc('errors.refresh')}
              </Button>
            }
          >
            {t('errorLoadingSickLeaves')}
          </Alert>
        ) : sickLeaves.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Typography color="text.secondary">{t('noSickLeaves')}</Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {sickLeaves.map((sl) => (
              <Card key={sl.id} variant="outlined">
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <UserAvatar name={sl.employeeName ?? '?'} size={36} />
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {sl.employeeName ?? t('unknownEmployee')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {sl.employeeEmail}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip label={t(sl.status)} color={getStatusColor(sl.status)} size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('startDate')}
                      </Typography>
                      <Typography variant="body2">{formatDate(sl.startDate)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('endDate')}
                      </Typography>
                      <Typography variant="body2">
                        {sl.endDate ? formatDate(sl.endDate) : t('ongoing')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('medicalCertificate')}
                      </Typography>
                      <Typography variant="body2">
                        {sl.hasMedicalCertificate ? t('yes') : t('no')}
                      </Typography>
                    </Box>
                  </Box>
                  {sl.reason && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('reason')}
                      </Typography>
                      <Typography variant="body2">{sl.reason}</Typography>
                    </Box>
                  )}
                </CardContent>
                {sl.status === 'active' && (
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    <Button size="small" onClick={() => setEndSickLeaveDialog(sl)}>
                      {t('endSickLeave')}
                    </Button>
                    <Button size="small" color="error" onClick={() => setCancelSickLeaveId(sl.id)}>
                      {t('cancel')}
                    </Button>
                  </CardActions>
                )}
              </Card>
            ))}
          </Stack>
        )}
      </TabPanel>

      {/* Vacation Requests Tab */}
      <TabPanel value={activeTab} index="vacation-requests">
        {/* Toolbar */}
        <Box
          // Bottom-aligned: a label-less search field is 16px shorter than a
          // labelled one (the theme reserves that room above the box), so
          // centring leaves the search box floating above its neighbours (#2259).
          sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 3, flexWrap: 'wrap' }}
        >
          <TextField
            size="small"
            placeholder={t('searchEmployee')}
            value={vacationSearch}
            onChange={(e) => setVacationSearch(e.target.value)}
            sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: 250 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              },
            }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('status')}</InputLabel>
            <Select
              value={vacationStatusFilter}
              label={t('status')}
              onChange={(e: SelectChangeEvent) =>
                setVacationStatusFilter(e.target.value as VacationRequestStatus | 'all')
              }
            >
              <MenuItem value="all">{t('allStatuses')}</MenuItem>
              <MenuItem value="pending">{t('pending')}</MenuItem>
              <MenuItem value="approved">{t('approved')}</MenuItem>
              <MenuItem value="declined">{t('declined')}</MenuItem>
              <MenuItem value="cancelled">{t('cancelled')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('type')}</InputLabel>
            <Select
              value={vacationTypeFilter}
              label={t('type')}
              onChange={(e: SelectChangeEvent) =>
                setVacationTypeFilter(e.target.value as VacationType | 'all')
              }
            >
              <MenuItem value="all">{t('allTypes')}</MenuItem>
              <MenuItem value="annual">{t('annual')}</MenuItem>
              <MenuItem value="unpaid">{t('unpaid')}</MenuItem>
              <MenuItem value="parental">{t('parental')}</MenuItem>
              <MenuItem value="study">{t('study')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Vacation Requests List */}
        {vacationRequestsQuery.isLoading ? (
          <Stack spacing={2}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
            ))}
          </Stack>
        ) : vacationRequestsQuery.isError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void vacationRequestsQuery.refetch()}>
                {tc('errors.refresh')}
              </Button>
            }
          >
            {t('errorLoadingVacations')}
          </Alert>
        ) : vacationRequests.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Typography color="text.secondary">
              {t('noVacationRequests')}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {vacationRequests.map((vr) => (
              <Card key={vr.id} variant="outlined">
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <UserAvatar name={vr.employeeName ?? '?'} size={36} />
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {vr.employeeName ?? t('unknownEmployee')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {vr.employeeEmail}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip label={t(vr.vacationType)} size="small" variant="outlined" />
                      <Chip label={t(vr.status)} color={getStatusColor(vr.status)} size="small" />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('startDate')}
                      </Typography>
                      <Typography variant="body2">{formatDate(vr.startDate)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('endDate')}
                      </Typography>
                      <Typography variant="body2">{formatDate(vr.endDate)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('totalDays')}
                      </Typography>
                      <Typography variant="body2">{vr.totalDays}</Typography>
                    </Box>
                  </Box>
                  {vr.notes && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('notes')}
                      </Typography>
                      <Typography variant="body2">{vr.notes}</Typography>
                    </Box>
                  )}
                  {vr.declineReason && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('declineReason')}
                      </Typography>
                      <Typography variant="body2" color="error">
                        {vr.declineReason}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
                {vr.status === 'pending' && (
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    <Button
                      size="small"
                      color="success"
                      startIcon={<CheckIcon />}
                      onClick={() => setApproveVacationId(vr.id)}
                      disabled={approveVacationMutation.isPending}
                    >
                      {t('approve')}
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<CloseIcon />}
                      onClick={() => setDeclineDialog(vr)}
                      disabled={declineVacationMutation.isPending}
                    >
                      {t('decline')}
                    </Button>
                  </CardActions>
                )}
                {(vr.status === 'pending' || vr.status === 'approved') && (
                  <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Button
                      size="small"
                      color="warning"
                      onClick={() => setCancelVacationId(vr.id)}
                      disabled={cancelVacationMutation.isPending}
                    >
                      {t('cancelRequest')}
                    </Button>
                  </CardActions>
                )}
              </Card>
            ))}
          </Stack>
        )}
      </TabPanel>

      {/* Work Hours Tab */}
      <TabPanel value={activeTab} index="work-hours">
        <WorkHoursTab />
      </TabPanel>

      {/* Skills Tab */}
      <TabPanel value={activeTab} index="skills">
        <SkillsTab />
      </TabPanel>

      {/* Shift Patterns Tab */}
      <TabPanel value={activeTab} index="shift-patterns">
        <ShiftPatternsTab />
      </TabPanel>

      <TabPanel value={activeTab} index="shift-end-confirmations">
        <ShiftEndConfirmationsTab />
      </TabPanel>

      {/* Team member dialogs */}
      <AddTeamMemberDialog
        open={addMemberOpen}
        onClose={() => setAddMemberOpen(false)}
        onSuccess={handleAddMemberSuccess}
      />

      <EditTeamMemberDialog
        open={!!editMember}
        member={editMember}
        onClose={() => { setEditMember(null); }}
        onSuccess={() => { void loadTeamMembers(); }}
      />

      <MobileJoinQrDialog target={qrTarget} onClose={() => { setQrTarget(null); }} />

      {/* End Sick Leave Dialog */}
      <ResponsiveDialog open={!!endSickLeaveDialog} onClose={() => setEndSickLeaveDialog(null)} maxWidth="xs" fullWidth mobileTitle={t('endSickLeave')}>
        <DialogTitle>{t('endSickLeave')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('confirmEndSickLeave')} {endSickLeaveDialog?.employeeName}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndSickLeaveDialog(null)}>{t('cancel')}</Button>
          <Button
            onClick={handleEndSickLeave}
            variant="contained"
            disabled={endSickLeaveMutation.isPending}
          >
            {endSickLeaveMutation.isPending ? <CircularProgress size={20} /> : t('confirm')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* Decline Vacation Dialog */}
      <ResponsiveDialog open={!!declineDialog} onClose={() => setDeclineDialog(null)} maxWidth="sm" fullWidth mobileTitle={t('declineVacation')}>
        <DialogTitle>{t('declineVacation')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t('declineVacationFor')} {declineDialog?.employeeName}?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('reason')}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            placeholder={t('enterDeclineReason')}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineDialog(null)}>{t('cancel')}</Button>
          <Button
            onClick={handleDeclineVacation}
            variant="contained"
            color="error"
            disabled={!declineReason.trim() || declineVacationMutation.isPending}
          >
            {declineVacationMutation.isPending ? <CircularProgress size={20} /> : t('decline')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* Cancel Sick Leave Confirmation */}
      <ConfirmDialog
        open={!!cancelSickLeaveId}
        title={t('confirmCancelSickLeave')}
        message={t('confirmCancelSickLeaveMessage')}
        confirmText={t('cancel')}
        confirmColor="error"
        onConfirm={() => {
          if (cancelSickLeaveId) void handleCancelSickLeave(cancelSickLeaveId);
          setCancelSickLeaveId(null);
        }}
        onCancel={() => setCancelSickLeaveId(null)}
      />

      {/* Approve Vacation Confirmation */}
      <ConfirmDialog
        open={!!approveVacationId}
        title={t('confirmApproveVacation')}
        message={t('confirmApproveVacationMessage')}
        confirmText={t('approve')}
        confirmColor="success"
        onConfirm={() => {
          if (approveVacationId) void handleApproveVacation(approveVacationId);
          setApproveVacationId(null);
        }}
        onCancel={() => setApproveVacationId(null)}
      />

      {/* Cancel Vacation Confirmation */}
      <ConfirmDialog
        open={!!cancelVacationId}
        title={t('confirmCancelVacation')}
        message={t('confirmCancelVacationMessage')}
        confirmText={t('cancelRequest')}
        confirmColor="error"
        onConfirm={() => {
          if (cancelVacationId) void handleCancelVacation(cancelVacationId);
          setCancelVacationId(null);
        }}
        onCancel={() => setCancelVacationId(null)}
      />
    </Box>
  );
}
